'use memo';

import {
  AgxButton,
  AgxColumn,
  AgxDropdown,
  AgxModal,
  AgxRow,
  AgxSegmentPicker,
  AgxTab,
  AgxTypography,
  Icon_AlertCircleOutline,
  Icon_Refresh,
  useToast,
  type DropdownOption,
} from '@urbanx/agx.ui-components2';
import {
  useStaffGetRexBrandAccountAgencies,
  useStaffQueueFastBulkRexResync,
  useStaffQueueFastBulkRexResyncForBrandAccount,
  useStaffQueueFullSlowRexResync,
  useStaffQueueFullSlowRexResyncForBrandAccount,
} from '@urbanx/agx.rex.client';
import LoadingState from 'components/loadingState/LoadingState';
import Panel from 'components/panel/Panel';
import { useState } from 'react';

import SupportBackButton from '../SupportBackButton';
import './CrmSupport.scss';

type CrmProviderTab = 'all' | 'rex';
type RexResyncMode = 'full-slow' | 'fast-bulk';

const crmProviderTabs = (selectedTab: CrmProviderTab) => [
  { id: 'all', label: 'All', selected: selectedTab === 'all' },
  { id: 'rex', label: 'Rex', selected: selectedTab === 'rex' },
];

const rexResyncSegments = [
  { title: 'Full slow resync', value: 'full-slow' },
  { title: 'Fast bulk resync', value: 'fast-bulk' },
];

const CrmSupport = () => {
  const { pushToast } = useToast();
  const [selectedProviderTab, setSelectedProviderTab] = useState<CrmProviderTab>('all');
  const [selectedRexMode, setSelectedRexMode] = useState<RexResyncMode>('full-slow');
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  const [showFullSlowResyncAllModal, setShowFullSlowResyncAllModal] = useState(false);

  const { data: brandAccountAgencies, isPending: isLoadingAgencies } =
    useStaffGetRexBrandAccountAgencies();

  const rexAgencies = (brandAccountAgencies ?? []).filter(
    (agency) => agency.agencyId && agency.brandAccountId && agency.brandAccountId > 0
  );

  const agencyOptions: DropdownOption[] = rexAgencies.map((agency) => ({
    label: agency.agencyName ?? 'Unnamed agency',
    value: agency.agencyId!,
    searchText: agency.agencyName ?? '',
  }));

  const selectedAgency = rexAgencies.find((agency) => agency.agencyId === selectedAgencyId);
  const selectedBrandAccountId = selectedAgency?.brandAccountId;

  const { staffQueueFullSlowRexResync, staffQueueFullSlowRexResyncPending } =
    useStaffQueueFullSlowRexResync({
      onSuccess: () => {
        setShowFullSlowResyncAllModal(false);
        pushToast({
          message: 'Full slow Rex resync queued',
          type: 'success',
        });
      },
      onError: () => {
        pushToast({
          message: 'Unable to queue full slow Rex resync',
          type: 'error',
        });
      },
    });

  const {
    staffQueueFullSlowRexResyncForBrandAccount,
    staffQueueFullSlowRexResyncForBrandAccountPending,
  } = useStaffQueueFullSlowRexResyncForBrandAccount({
    onSuccess: () => {
      pushToast({
        message: 'Full slow Rex brand account resync queued',
        type: 'success',
      });
    },
    onError: () => {
      pushToast({
        message: 'Unable to queue Rex brand account full slow resync',
        type: 'error',
      });
    },
  });

  const { staffQueueFastBulkRexResync, staffQueueFastBulkRexResyncPending } =
    useStaffQueueFastBulkRexResync({
      onSuccess: () => {
        pushToast({
          message: 'Fast bulk Rex resync queued',
          type: 'success',
        });
      },
      onError: () => {
        pushToast({
          message: 'Unable to queue fast bulk Rex resync',
          type: 'error',
        });
      },
    });

  const {
    staffQueueFastBulkRexResyncForBrandAccount,
    staffQueueFastBulkRexResyncForBrandAccountPending,
  } = useStaffQueueFastBulkRexResyncForBrandAccount({
    onSuccess: () => {
      pushToast({
        message: 'Fast bulk Rex brand account resync queued',
        type: 'success',
      });
    },
    onError: () => {
      pushToast({
        message: 'Unable to queue Rex brand account fast bulk resync',
        type: 'error',
      });
    },
  });

  const queueFullSlowBrandAccountResync = () => {
    if (!selectedBrandAccountId) return;

    staffQueueFullSlowRexResyncForBrandAccount({
      brandAccountId: selectedBrandAccountId,
    });
  };

  const queueFastBulkBrandAccountResync = () => {
    if (!selectedBrandAccountId) return;

    staffQueueFastBulkRexResyncForBrandAccount({
      brandAccountId: selectedBrandAccountId,
    });
  };

  const isSingleAgencyActionDisabled =
    !selectedBrandAccountId ||
    staffQueueFullSlowRexResyncForBrandAccountPending ||
    staffQueueFastBulkRexResyncForBrandAccountPending;

  return (
    <AgxColumn gap="spacing-2xl" fill>
      <SupportBackButton />

      <AgxColumn gap="spacing-sm" fill>
        <AgxTypography id="crm-support-heading" type="heading" size="large">
          CRM Support
        </AgxTypography>
        <AgxTypography id="crm-support-description" type="body" size="medium">
          Address CRM issues, queue data resyncs, and manage CRM recovery actions for connected
          systems.
        </AgxTypography>
      </AgxColumn>

      <AgxTab
        options={crmProviderTabs(selectedProviderTab)}
        onChange={(tab) => setSelectedProviderTab(tab.id as CrmProviderTab)}
      />

      {selectedProviderTab === 'all' && (
        <Panel title="CRM tools">
          <AgxTypography id="crm-support-all-empty" type="body" size="small">
            No CRM-wide support tools are available yet.
          </AgxTypography>
        </Panel>
      )}

      {selectedProviderTab === 'rex' && (
        <AgxColumn gap="spacing-xl" fill>
          <AgxSegmentPicker
            id="crm-support-rex-mode"
            segments={rexResyncSegments}
            defaultValue={selectedRexMode}
            onChange={(value) => setSelectedRexMode(value as RexResyncMode)}
          />

          {selectedRexMode === 'full-slow' && (
            <>
              <Panel
                title="Resync all"
                actionContent={
                  <AgxButton
                    id="crm-support-full-slow-all"
                    text={staffQueueFullSlowRexResyncPending ? 'Queueing' : 'Full resync'}
                    variant="primary"
                    size="small"
                    danger
                    leftIcon={<Icon_Refresh width={16} height={16} />}
                    disabled={staffQueueFullSlowRexResyncPending}
                    onClick={() => setShowFullSlowResyncAllModal(true)}
                  />
                }
              >
                <AgxTypography id="crm-support-full-slow-all-description" type="body" size="small">
                  Queue a complete slow resync for every Rex brand account.
                </AgxTypography>
              </Panel>

              <Panel title="Resync brand account">
                <BrandAccountResyncControls
                  selectedAgencyId={selectedAgencyId}
                  agencyOptions={agencyOptions}
                  isLoadingAgencies={isLoadingAgencies}
                  isActionDisabled={isSingleAgencyActionDisabled}
                  isActionPending={staffQueueFullSlowRexResyncForBrandAccountPending}
                  buttonText="Full resync"
                  pendingButtonText="Queueing"
                  onAgencyChange={setSelectedAgencyId}
                  onResync={queueFullSlowBrandAccountResync}
                />
              </Panel>
            </>
          )}

          {selectedRexMode === 'fast-bulk' && (
            <>
              <Panel
                title="Resync all"
                actionContent={
                  <AgxButton
                    id="crm-support-fast-bulk-all"
                    text={staffQueueFastBulkRexResyncPending ? 'Queueing' : 'Fast bulk resync'}
                    variant="primary"
                    size="small"
                    leftIcon={<Icon_Refresh width={16} height={16} />}
                    disabled={staffQueueFastBulkRexResyncPending}
                    onClick={() => staffQueueFastBulkRexResync()}
                  />
                }
              >
                <AgxTypography id="crm-support-fast-bulk-all-description" type="body" size="small">
                  Queue Rex bulk resync events for all known brand accounts.
                </AgxTypography>
              </Panel>

              <Panel title="Resync brand account">
                <BrandAccountResyncControls
                  selectedAgencyId={selectedAgencyId}
                  agencyOptions={agencyOptions}
                  isLoadingAgencies={isLoadingAgencies}
                  isActionDisabled={isSingleAgencyActionDisabled}
                  isActionPending={staffQueueFastBulkRexResyncForBrandAccountPending}
                  buttonText="Fast bulk resync"
                  pendingButtonText="Queueing"
                  onAgencyChange={setSelectedAgencyId}
                  onResync={queueFastBulkBrandAccountResync}
                />
              </Panel>
            </>
          )}
        </AgxColumn>
      )}

      <AgxModal
        id="crm-support-full-slow-resync-modal"
        title="Confirm full Rex resync"
        titleIcon={
          <div className="crmSupport__modalIcon">
            <Icon_AlertCircleOutline type="Danger" height={40} width={40} />
          </div>
        }
        type="single"
        variant="withDivider"
        actionButtonContainerStyle="withDivider"
        isOpen={showFullSlowResyncAllModal}
        setIsDialogOpen={setShowFullSlowResyncAllModal}
        actionButtons={
          <AgxRow gap="spacing-md" justifyContent="center" fill>
            <AgxButton
              id="crm-support-full-slow-cancel"
              text="Cancel"
              variant="hollow"
              stretch
              disabled={staffQueueFullSlowRexResyncPending}
              onClick={() => setShowFullSlowResyncAllModal(false)}
            />
            <AgxButton
              id="crm-support-full-slow-confirm"
              text={staffQueueFullSlowRexResyncPending ? 'Queueing' : 'Confirm'}
              variant="primary"
              danger
              stretch
              disabled={staffQueueFullSlowRexResyncPending}
              onClick={() => staffQueueFullSlowRexResync()}
            />
          </AgxRow>
        }
        content={
          <AgxColumn gap="spacing-md">
            <AgxTypography id="crm-support-full-slow-warning" type="body" size="medium">
              This is a complete resync of all Rex data. This will take a significant amount of time
              and will prevent new records from being registered in our system while this sync is in
              place. They will eventually appear, but there will be a significant delay before new
              data appears.
            </AgxTypography>
          </AgxColumn>
        }
      />
    </AgxColumn>
  );
};

interface BrandAccountResyncControlsProps {
  selectedAgencyId: string;
  agencyOptions: DropdownOption[];
  isLoadingAgencies: boolean;
  isActionDisabled: boolean;
  isActionPending: boolean;
  buttonText: string;
  pendingButtonText: string;
  onAgencyChange: (agencyId: string) => void;
  onResync: () => void;
}

const BrandAccountResyncControls = ({
  selectedAgencyId,
  agencyOptions,
  isLoadingAgencies,
  isActionDisabled,
  isActionPending,
  buttonText,
  pendingButtonText,
  onAgencyChange,
  onResync,
}: BrandAccountResyncControlsProps) => {
  return (
    <AgxColumn gap="spacing-lg" fill>
      <div className="crmSupport__brandAccountControls">
        <AgxRow gap="spacing-lg" fill wrap alignItems="flex-end">
          <div className="crmSupport__agencyDropdown">
            <AgxDropdown
              id="crm-support-rex-agency"
              label="Agency"
              selected={selectedAgencyId}
              options={agencyOptions}
              onChange={onAgencyChange}
              placeholder="Select agency"
              searchable
              searchPlaceholder="Search agencies"
              stretch
              hideOptionalText
              disabled={isLoadingAgencies || isActionPending}
            />
          </div>

          <AgxButton
            id="crm-support-rex-brand-account-resync"
            text={isActionPending ? pendingButtonText : buttonText}
            variant="primary"
            size="medium"
            leftIcon={<Icon_Refresh width={16} height={16} />}
            disabled={isActionDisabled}
            onClick={onResync}
          />
        </AgxRow>
      </div>

      {isLoadingAgencies && <LoadingState />}

      {!isLoadingAgencies && agencyOptions.length === 0 && (
        <AgxTypography id="crm-support-rex-no-agencies" type="body" size="small">
          No agencies with Rex brand accounts are available.
        </AgxTypography>
      )}
    </AgxColumn>
  );
};

export default CrmSupport;
